import {InvalidTokenError} from '@packages/utils';
import {parseCookies} from 'nookies';
import {useCallback, useEffect, useState} from 'react';
import {
  deleteLikedCommunityComment,
  postLikedCommunityComment,
} from '../../repositories/communityRepository';
import {validateCookie} from '../auth/checkOauthTokenExistence';
import {useIsSessionExpired} from '../auth/useIsSessionExpired';

type Props = {
  topicId: number;
  seqNumber: number;
  likedCountInitial: number;
  isLikedInitial: boolean;
};

export function useHandleLikedCommunityComment({
  topicId,
  seqNumber,
  likedCountInitial,
  isLikedInitial,
}: Props) {
  const cookies = parseCookies();
  const {accessToken} = validateCookie(cookies);

  const {updateIsSessionExpired} = useIsSessionExpired();
  const [likedCount, setLikedCount] = useState<number>(likedCountInitial);
  const [isLiked, setIsLiked] = useState<boolean>(isLikedInitial);

  useEffect(() => {
    setLikedCount(likedCountInitial);
    setIsLiked(isLikedInitial);
  }, [topicId, seqNumber, likedCountInitial, isLikedInitial]);

  const deleteLikedUsecase = useCallback(
    async function deleteLikedUsecase() {
      const deleteData = await deleteLikedCommunityComment({
        cookies,
        topicId,
        seqNumber,
      });

      if (!deleteData.ok && deleteData.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!deleteData.ok) {
        return;
      }

      setIsLiked(false);
      setLikedCount(deleteData.value.likeCount);
    },
    [topicId, accessToken],
  );

  const postLikedUsecase = useCallback(
    async function postLikedUsecase() {
      const updateData = await postLikedCommunityComment({
        cookies,
        topicId,
        seqNumber,
      });

      if (!updateData.ok && updateData.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      if (!updateData.ok) {
        return;
      }

      setIsLiked(true);
      setLikedCount(updateData.value.likeCount);
    },
    [topicId, accessToken],
  );

  const handleLikedCommunityComment = useCallback(async () => {
    if (isLiked) {
      await deleteLikedUsecase();
      return;
    }

    await postLikedUsecase();
  }, [isLiked, deleteLikedUsecase, postLikedUsecase]);

  return {
    handleLikedCommunityComment,
    likedCount,
    isLiked,
  };
}
